import React from 'react';
import { useAuthContext } from 'Shared/Security/Domain/Contexts/authContext';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Table, 
  TableHead, 
  TableRow, 
  TableCell, 
  TableBody, 
  Paper, 
  TableContainer, 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  TextField, 
  Grid,
  IconButton,
  Tooltip,
  CircularProgress,
  Switch,
  FormControlLabel,
  Backdrop
} from '@mui/material';
import { Autocomplete } from '@mui/material';
import { getDepartmentsList } from 'Department/Domain/Services/GetDepartmentsListService';
import { 
  MeetingRoom as MeetingIcon, 
  Description as ActaIcon, 
  Cancel as RejectIcon, 
  CheckCircle as CloseIcon,
  Visibility as ViewDocsIcon,
  FilterList as FilterIcon,
  ArrowBack as ArrowBackIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { IMPROVEMENT_CHANNEL } from 'User/Application/Constants/PathsConstant';
import { getImprovementProposalList } from 'ImprovementProposal/Domain/Services/GetImprovementProposalListService';
import { patchImprovementProposal } from 'ImprovementProposal/Domain/Services/PatchImprovementProposalService';
import { getImprovementProposalMeetings } from 'ImprovementProposal/Domain/Services/GetImprovementProposalMeetingsService';
import { createImprovementProposalMeeting } from 'ImprovementProposal/Domain/Services/Meeting/CreateImprovementProposalMeetingService';
import { deleteImprovementProposalMeeting } from 'ImprovementProposal/Domain/Services/Meeting/DeleteImprovementProposalMeetingService';
import { stopRecurrenceImprovementProposalMeeting } from 'ImprovementProposal/Domain/Services/Meeting/StopRecurrenceImprovementProposalMeetingService';
import { getDocuments } from 'Document/Domain/Services/GetDocumentsService';
import { addDocument } from 'Document/Domain/Services/AddDocumentService';
import { downloadDocument } from 'Document/Domain/Services/DownloadDocumentService';
import Loader from 'UI/Application/Components/Loaders/Loader';
import { toast } from 'react-toastify';

export default function ImprovementProposalTrackingView() {
  const { isAdmin, getMeFromLocalStorage } = useAuthContext();
  const navigate = useNavigate();
  const [proposals, setProposals] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [meetingsByProposal, setMeetingsByProposal] = React.useState({});
  // Filtros siempre visibles
  
  // Dialogs
  const [openMeetingDialog, setOpenMeetingDialog] = React.useState(false);
  const [openActaDialog, setOpenActaDialog] = React.useState(false);
  const [openRejectDialog, setOpenRejectDialog] = React.useState(false);
  const [openCloseDialog, setOpenCloseDialog] = React.useState(false);
  const [openDocsDialog, setOpenDocsDialog] = React.useState(false);
  const [selectedProposal, setSelectedProposal] = React.useState(null);
  const [selectedMeeting, setSelectedMeeting] = React.useState(null);
  
  // Form states
  const [meetingForm, setMeetingForm] = React.useState({
    meetingDate: '',
    recurrenceDays: 0,
    description: ''
  });
  const [closeForm, setCloseForm] = React.useState({
    timeReduction: false,
    hoursPerUnit: 0,
    unitsPerYear: 0,
    costReduction: false,
    eurosPerUnit: 0,
    unitsPerYearCost: 0
  });
  const [rejectionReason, setRejectionReason] = React.useState('');
  const [documents, setDocuments] = React.useState([]);
  const [actas, setActas] = React.useState([]);
  // Actas previas agrupadas por reunión de origen [{ meetingId, meetingDate, docs: Document[] }]
  const [prevActaGroups, setPrevActaGroups] = React.useState([]);
  const [uploadingActa, setUploadingActa] = React.useState(false);
  const [uploadTotal, setUploadTotal] = React.useState(0);
  const [uploadDone, setUploadDone] = React.useState(0);
  const [actionLoading, setActionLoading] = React.useState(false);
  const [actionMessage, setActionMessage] = React.useState('');
  // Eliminamos prefetch de nombres; usamos trackingUserName directamente
  const canManage = (p) => {
    if (!p) return false;
    const admin = (isAdmin && typeof isAdmin === 'function') ? isAdmin() : false;
    if (admin) return true;
    const me = getMeFromLocalStorage?.();
    if (!me?.id) return false;
    return Number(p.trackingUserId) === Number(me.id);
  };

  const getAssignedName = (p) => (p?.trackingUserName || p?.trackingUserId || '');

  const getCreatorName = (p) => {
    if (!p) return '';
    if (p.createdByUserName) return p.createdByUserName;
    const u = p.createdByUser;
    if (u && (u.name || u.firstSurname)) {
      return `${u.name || ''} ${u.firstSurname || ''}`.trim();
    }
    return '';
  };
  
  // Ver documento/acta en nueva pestaña (patrón FirstStep)
  const handleDownloadDocument = async (doc) => {
    const result = await downloadDocument(doc.path);
    if (!result || result.contentType.includes('application/json')) {
      toast.error('No se pudo descargar el archivo');
      return;
    }
    const url = window.URL.createObjectURL(result.blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = doc.fileName || 'documento';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };
  
  // Filtros
  const [filterId, setFilterId] = React.useState('');
  const [filterDateFrom, setFilterDateFrom] = React.useState('');
  const [filterDateTo, setFilterDateTo] = React.useState('');
  const [filterCreator, setFilterCreator] = React.useState('');
  const [filterAssigned, setFilterAssigned] = React.useState('');
  const [filterDescription, setFilterDescription] = React.useState('');
  const [filterGoal, setFilterGoal] = React.useState('');
  const [filterProcess, setFilterProcess] = React.useState('');
  const [filterDepartments, setFilterDepartments] = React.useState([]); // array<int>

  // Departamentos disponibles
  const [departments, setDepartments] = React.useState([]); // [{id,name}]
  const departmentMap = React.useMemo(() => {
    const map = new Map();
    departments.forEach(d => map.set(d.id, d.name));
    return map;
  }, [departments]);

  React.useEffect(() => {
    (async () => {
      try {
        const resp = await getDepartmentsList({ limit: 500, offset: 0 });
        const list = Array.isArray(resp?.records) ? resp.records : [];
        setDepartments(list.map(r => ({ id: r.id, name: r.name || r.description || `Dept ${r.id}` })));
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Error cargando departamentos', e);
      }
    })();
  }, []);

  // Opciones únicas para autocompletar en filtros
  const creatorOptions = React.useMemo(() => {
    const set = new Set(
      (proposals || [])
        .map(p => (getCreatorName(p) || '').toString().trim())
        .filter(Boolean)
    );
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [proposals]);

  const assignedOptions = React.useMemo(() => {
    const set = new Set(
      (proposals || [])
        .map(p => (getAssignedName(p) || '').toString().trim())
        .filter(Boolean)
    );
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [proposals]);

  const loadProposals = React.useCallback(async () => {
    try {
      const response = await getImprovementProposalList({ limit: 100, offset: 0 });
      const records = response?.records || [];
      const acceptedProposals = records.filter(p => p.status === 'accepted');
      setProposals(acceptedProposals);
      const meetingsEntries = await Promise.all(acceptedProposals.map(async (p) => [p.id, await getImprovementProposalMeetings(p.id)]));
      setMeetingsByProposal(Object.fromEntries(meetingsEntries));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Error loading proposals:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMeetingsForProposal = async (proposalId) => {
    const meetings = await getImprovementProposalMeetings(proposalId);
    setMeetingsByProposal((prev) => ({ ...prev, [proposalId]: meetings || [] }));
  };

  
  React.useEffect(() => {
      loadProposals();
  }, [loadProposals]);

  const handleCreateMeeting = async () => {
    const ok = await createImprovementProposalMeeting(selectedProposal.id, meetingForm);
    if (ok) {
      toast.success('Reunión creada correctamente');
      await loadMeetingsForProposal(selectedProposal.id);
      setMeetingForm({ meetingDate: '', recurrenceDays: meetingForm.recurrenceDays, description: meetingForm.description });
    } else {
      toast.error('Error al crear la reunión');
    }
  };

  const handleDeleteMeeting = async (proposalId, meetingId) => {
    const ok = await deleteImprovementProposalMeeting(meetingId);
    if (ok) {
      toast.success('Reunión eliminada correctamente');
      await loadMeetingsForProposal(proposalId);
    } else {
      toast.error('Error al eliminar la reunión');
    }
  };

  const handleStopRecurrence = async (meetingId) => {
    const ok = await stopRecurrenceImprovementProposalMeeting(meetingId);
    if (ok) {
      toast.success('Recurrencia detenida correctamente');
      if (selectedProposal?.id) await loadMeetingsForProposal(selectedProposal.id);
    } else {
      toast.error('Error al detener la recurrencia');
    }
  };

  const handleViewDocuments = async (proposal) => {
    setActionMessage('Cargando documentos...');
    setActionLoading(true);
    const docs = await getDocuments({ className: 'ImprovementProposal', idClassName: proposal.id });
    setDocuments(docs || []);
    setSelectedProposal(proposal);
    setOpenDocsDialog(true);
    setActionLoading(false);
    setActionMessage('');
  };

  const handleViewActas = async (meeting) => {
    const docs = await getDocuments({ className: 'ImprovementProposalMeeting', idClassName: meeting.id });
    setActas(docs || []);
    const meetings = meetingsByProposal[selectedProposal?.id] || [];
    const byId = new Map(meetings.map(m => [m.id, m]));
    const chain = [];
    let cursor = meeting.previousMeeting?.id;
    const guard = new Set();
    while (cursor && !guard.has(cursor)) {
      guard.add(cursor);
      const prev = byId.get(cursor);
      if (!prev) break;
      chain.push(prev);
      cursor = prev.previousMeeting?.id;
    }
    chain.reverse();
    const groups = await Promise.all(chain.map(async (prev) => {
      const prevDocs = await getDocuments({ className: 'ImprovementProposalMeeting', idClassName: prev.id });
      return { meetingId: prev.id, meetingDate: prev.meetingDate, docs: prevDocs || [] };
    }));
    setPrevActaGroups(groups);
    setSelectedMeeting(meeting);
    setOpenActaDialog(true);
  };

  const handleUploadActa = async (event) => {
    if (!selectedMeeting) return;
    // Sólo permitir subida si el usuario puede gestionar la propuesta
    if (!canManage(selectedProposal)) {
      toast.error('No tienes permisos para subir actas');
      event.target.value = '';
      return;
    }
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    try {
      setUploadingActa(true);
      setUploadTotal(files.length);
      setUploadDone(0);
      for (const [index, file] of files.entries()) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve, reject) => {
          const reader = new FileReader();
            reader.onload = async () => {
              try {
                const base64 = reader.result;
                const timestamp = Date.now();
                const originalName = file?.name || `IPM-${selectedMeeting.id}-${timestamp}-${index}`;
                const ext = originalName.includes('.') ? originalName.split('.').pop() : 'pdf';
                await addDocument({
                  className: 'ImprovementProposalMeeting',
                  idClassName: selectedMeeting.id,
                  fileName: `IPM-${selectedMeeting.id}-${timestamp}-${index}.${ext}`,
                  base64DataUrl: base64
                });
                setUploadDone((v) => v + 1);
                resolve();
              } catch (e) { 
                reject(e); 
              }
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
      }
      const docs = await getDocuments({ className: 'ImprovementProposalMeeting', idClassName: selectedMeeting.id });
      setActas(docs || []);
      toast.success('Acta(s) subidas correctamente');
    } catch (e) {
      toast.error('Error al subir acta');
    } finally {
      setUploadingActa(false);
      setUploadTotal(0);
      setUploadDone(0);
      event.target.value = '';
    }
  };

  const handleReject = async () => {
    setActionMessage('Rechazando...');
    setActionLoading(true);
    const result = await patchImprovementProposal(selectedProposal.id, { workflowAction: 'reject', rejectionReason });
    if (result) {
      toast.success('Propuesta rechazada correctamente');
      setOpenRejectDialog(false);
      setSelectedProposal(null);
      setRejectionReason('');
      loadProposals();
    } else {
      toast.error('Error al rechazar la propuesta');
    }
    setActionLoading(false);
    setActionMessage('');
  };

  const handleClose = async () => {
    setActionMessage('Cerrando...');
    setActionLoading(true);
    const data = { workflowAction: 'close' };
    if (closeForm.timeReduction) {
      const hours = parseFloat(closeForm.hoursPerUnit || 0);
      const units = parseFloat(closeForm.unitsPerYear || 0);
      const totalHours = Math.round(hours * units);
      data.timeReduction = Number.isFinite(totalHours) ? totalHours : 0;
      // Guardar también el dato base utilizado para el cálculo
      const hoursPerUnit = parseFloat(closeForm.hoursPerUnit || 0);
      data.timeIncrease = Number.isFinite(hoursPerUnit) ? Math.round(hoursPerUnit) : 0;
      data.timeUnitsPerYear = Number.isFinite(units) ? Math.round(units) : 0;
    } else {
      data.timeReduction = null;
      data.timeIncrease = null;
      data.timeUnitsPerYear = null;
    }
    if (closeForm.costReduction) {
      const euros = parseFloat(closeForm.eurosPerUnit || 0);
      const unitsCost = parseFloat(closeForm.unitsPerYearCost || 0);
      const totalEuros = euros * unitsCost;
      data.costReduction = Number.isFinite(totalEuros) ? totalEuros.toFixed(2) : '0.00';
      // Guardar también el dato base utilizado para el cálculo
      const eurosPerUnit = parseFloat(closeForm.eurosPerUnit || 0);
      data.costIncrease = Number.isFinite(eurosPerUnit) ? eurosPerUnit.toFixed(2) : '0.00';
      data.costUnitsPerYear = Number.isFinite(unitsCost) ? Math.round(unitsCost) : 0;
    } else {
      data.costReduction = null;
      data.costIncrease = null;
      data.costUnitsPerYear = null;
    }
    const result = await patchImprovementProposal(selectedProposal.id, data);
    if (result) {
      toast.success('Propuesta cerrada correctamente');
      setOpenCloseDialog(false);
      setSelectedProposal(null);
      setCloseForm({
        timeReduction: false,
        hoursPerUnit: 0,
        unitsPerYear: 0,
        costReduction: false,
        eurosPerUnit: 0,
        unitsPerYearCost: 0
      });
      loadProposals();
    } else {
      toast.error('Error al cerrar la propuesta');
    }
    setActionLoading(false);
    setActionMessage('');
  };

  const filteredProposals = proposals.filter(proposal => {
    if (filterId && !proposal.id.toString().includes(filterId)) return false;
    if (filterDateFrom) {
      const from = new Date(`${filterDateFrom}T00:00:00`);
      if (new Date(proposal.createdAt) < from) return false;
    }
    if (filterDateTo) {
      const to = new Date(`${filterDateTo}T23:59:59.999`);
      if (new Date(proposal.createdAt) > to) return false;
    }
    if (filterCreator) {
      const creator = (getCreatorName(proposal) || '').toLowerCase();
      if (!creator.includes(filterCreator.toLowerCase())) return false;
    }
    if (filterAssigned) {
      const assigned = (getAssignedName(proposal) || '').toLowerCase();
      if (!assigned.includes(filterAssigned.toLowerCase())) return false;
    }
    if (filterDescription && !proposal.currentSituationDescription?.toLowerCase().includes(filterDescription.toLowerCase())) return false;
    if (filterGoal && !proposal.improvementGoal?.toLowerCase().includes(filterGoal.toLowerCase())) return false;
    if (filterProcess) {
      const proc = (proposal.relatedProcess || '').toLowerCase();
      if (!proc.includes(filterProcess.toLowerCase())) return false;
    }
    if (filterDepartments.length > 0) {
      const rel = Array.isArray(proposal.relatedDepartments) ? proposal.relatedDepartments : [];
      const hasAny = rel.some(id => filterDepartments.includes(id));
      if (!hasAny) return false;
    }
    return true;
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Loader />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box display="flex" alignItems="center" mb={1}>
        <IconButton size="small" onClick={() => navigate(IMPROVEMENT_CHANNEL)}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="body2" sx={{ ml: 1, cursor: 'pointer' }} onClick={() => navigate(IMPROVEMENT_CHANNEL)}>Volver</Typography>
      </Box>
      <Typography variant="h5" mb={2}>Seguimiento / Cierre mejoras en curso</Typography>
      
      {/* Filtros (siempre visibles) */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" alignItems="center" mb={2}>
          <FilterIcon sx={{ mr: 1 }} />
          <Typography variant="h6">Filtros</Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} md={2}>
            <TextField 
              fullWidth 
              size="small" 
              label="ID" 
              value={filterId} 
              onChange={(e) => setFilterId(e.target.value)} 
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField 
              fullWidth 
              size="small" 
              type="date" 
              label="Fecha desde" 
              value={filterDateFrom} 
              onChange={(e) => setFilterDateFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField 
              fullWidth 
              size="small" 
              type="date" 
              label="Fecha hasta" 
              value={filterDateTo} 
              onChange={(e) => setFilterDateTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Autocomplete
              size="small"
              options={creatorOptions}
              value={filterCreator || null}
              onChange={(e, v) => setFilterCreator(v || '')}
              renderInput={(params) => (
                <TextField {...params} fullWidth label="Creador" />
              )}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Autocomplete
              size="small"
              options={assignedOptions}
              value={filterAssigned || null}
              onChange={(e, v) => setFilterAssigned(v || '')}
              renderInput={(params) => (
                <TextField {...params} fullWidth label="Asignado a" />
              )}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField 
              fullWidth 
              size="small" 
              label="Descripción situación" 
              value={filterDescription} 
              onChange={(e) => setFilterDescription(e.target.value)} 
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField 
              fullWidth 
              size="small" 
              label="¿Qué se pretende mejorar?" 
              value={filterGoal} 
              onChange={(e) => setFilterGoal(e.target.value)} 
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Proceso relacionado"
              value={filterProcess}
              onChange={(e) => setFilterProcess(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Autocomplete
              multiple
              size="small"
              options={departments}
              getOptionLabel={(o) => o.name || ''}
              value={departments.filter(d => filterDepartments.includes(d.id))}
              onChange={(e, values) => setFilterDepartments(values.map(v => v.id))}
              renderInput={(params) => (
                <TextField {...params} fullWidth label="Departamentos" placeholder="Filtrar..." />
              )}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Tabla */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell align="center">ID</TableCell>
              <TableCell align="center">Fecha</TableCell>
              <TableCell align="center">Creado por</TableCell>
              <TableCell align="center">Asignado a</TableCell>
              <TableCell align="center">Proceso relacionado</TableCell>
              <TableCell align="center">Departamentos</TableCell>
              <TableCell align="center">Descripción situación</TableCell>
              <TableCell align="center">¿Qué se pretende mejorar?</TableCell>
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredProposals.map((proposal) => (
              <TableRow key={proposal.id}>
                <TableCell align="center">
                  <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center">
                    <Tooltip title="Ver detalle de la propuesta">
                      <Button
                        variant="text"
                        size="small"
                        sx={{ mt: 0.5, textTransform: 'none' }}
                        onClick={() => navigate(`/private/user/improvement-proposal/${proposal.id}`)}
                      >
                        {proposal.id}
                      </Button>
                    </Tooltip>
                  </Box>
                </TableCell>
                <TableCell align="center">
                  {new Date(proposal.createdAt).toLocaleDateString()}
                </TableCell>
                <TableCell align="center">{getCreatorName(proposal) || 'N/A'}</TableCell>
                <TableCell align="center">{getAssignedName(proposal) || 'N/A'}</TableCell>
                <TableCell align="center">{proposal.relatedProcess || '-'}</TableCell>
                <TableCell align="center">
                  {Array.isArray(proposal.relatedDepartments) && proposal.relatedDepartments.length > 0 ? (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center' }}>
                      {proposal.relatedDepartments.map(id => {
                        const name = departmentMap.get(id) || id;
                        return (
                          <Box
                            key={id}
                            component="span"
                            sx={{
                              px: 1.2,
                              py: 0.4,
                              fontSize: '0.65rem',
                              lineHeight: 1.2,
                              bgcolor: 'grey.200',
                              color: 'grey.900',
                              borderRadius: '999px',
                              fontWeight: 500,
                              display: 'inline-block',
                              maxWidth: '140px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                            title={String(name)}
                          >
                            {name}
                          </Box>
                        );
                      })}
                    </Box>
                  ) : (
                    '-'
                  )}
                </TableCell>
                <TableCell align="center">
                  {proposal.currentSituationDescription || '-'}
                </TableCell>
                <TableCell align="center">
                  {proposal.improvementGoal || '-'}
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Reuniones">
                    <IconButton
                      color="primary"
                      onClick={async () => {
                        setSelectedProposal(proposal);
                        await loadMeetingsForProposal(proposal.id);
                        setOpenMeetingDialog(true);
                      }}
                    >
                      <MeetingIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Ver documentos">
                    <IconButton
                      color="info"
                      disabled={actionLoading}
                      onClick={() => handleViewDocuments(proposal)}
                    >
                      <ViewDocsIcon />
                    </IconButton>
                  </Tooltip>
                  {canManage(proposal) && (
                    <>
                      <Tooltip title="Rechazar">
                        <IconButton
                          color="error"
                          disabled={actionLoading}
                          onClick={() => {
                            setSelectedProposal(proposal);
                            setOpenRejectDialog(true);
                          }}
                        >
                          <RejectIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Cerrar">
                        <IconButton
                          color="success"
                          sx={{ '&:hover': { color: 'success.main' } }}
                          disabled={actionLoading}
                          onClick={() => {
                            setSelectedProposal(proposal);
                            setOpenCloseDialog(true);
                          }}
                        >
                          <CloseIcon />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog Reuniones */}
      <Dialog open={openMeetingDialog} onClose={() => setOpenMeetingDialog(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Gestión de Reuniones - Propuesta #{selectedProposal?.id}</DialogTitle>
        <DialogContent>
          {canManage(selectedProposal) && (
            <Box mb={3}>
              <Typography variant="h6" mb={2}>Programar nueva reunión</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    type="datetime-local"
                    label="Fecha inicio"
                    value={meetingForm.meetingDate}
                    onChange={(e) => setMeetingForm({...meetingForm, meetingDate: e.target.value})}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Recurrencia en días (0 = única)"
                    value={meetingForm.recurrenceDays}
                    onChange={(e) => setMeetingForm({...meetingForm, recurrenceDays: e.target.value})}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Descripción"
                    value={meetingForm.description}
                    onChange={(e) => setMeetingForm({...meetingForm, description: e.target.value})}
                  />
                </Grid>
              </Grid>
              <Button 
                variant="contained" 
                startIcon={<AddIcon />}
                onClick={handleCreateMeeting}
                sx={{ mt: 2 }}
                disabled={!meetingForm.meetingDate}
              >
                Programar reunión
              </Button>
            </Box>
          )}
          
          <Typography variant="h6" mb={2}>Reuniones programadas</Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Fecha inicio</TableCell>
                  <TableCell>Fecha fin</TableCell>
                  <TableCell>Recurrencia</TableCell>
                  <TableCell>Descripción</TableCell>
                  <TableCell>Actas</TableCell>
                  {canManage(selectedProposal) && <TableCell>Acciones</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {(meetingsByProposal[selectedProposal?.id] || []).map((meeting) => (
                  <TableRow key={meeting.id}>
                    <TableCell>{new Date(meeting.meetingDate).toLocaleString()}</TableCell>
                    <TableCell>
                      {meeting.recurrenceEndAt ? new Date(meeting.recurrenceEndAt).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell>{meeting.recurrenceNote || (meeting.recurrenceDays > 0 ? `${meeting.recurrenceDays} días` : 'Única')}</TableCell>
                    <TableCell>{meeting.description || '-'}</TableCell>
                    <TableCell>
                      <Tooltip title="Actas">
                        <IconButton size="small" onClick={() => handleViewActas(meeting)}>
                          <ActaIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                    {canManage(selectedProposal) && (
                      <TableCell>
                        {meeting.recurrenceDays > 0 && !meeting.recurrenceEndAt && (
                          <Tooltip title="Finalizar recurrencia">
                            <IconButton size="small" onClick={() => handleStopRecurrence(meeting.id)}>
                              <CloseIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Eliminar">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteMeeting(selectedProposal.id, meeting.id)}
                          >
                            <RejectIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenMeetingDialog(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Actas */}
      <Dialog open={openActaDialog} onClose={() => setOpenActaDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Actas - Reunión #{selectedMeeting?.id}</DialogTitle>
        <DialogContent>
          {canManage(selectedProposal) && (
            <Box mb={2}>
              <Button variant="contained" component="label" disabled={!selectedMeeting || uploadingActa}>
                Subir acta
                <input type="file" hidden multiple onChange={handleUploadActa} />
              </Button>
            </Box>
          )}
          <Box mb={2}>
            {prevActaGroups.length === 0 ? (
              <Typography variant="body2">Sin actas previas</Typography>
            ) : (
              prevActaGroups.map((group) => (
                <Box key={`group-${group.meetingId}`} mb={2} p={2} border={1} borderColor="divider" borderRadius={1}>
                  <Typography variant="subtitle2" gutterBottom>
                    Viene de la reunión del {new Date(group.meetingDate).toLocaleString()} (#{group.meetingId})
                  </Typography>
                  {group.docs.length === 0 ? (
                    <Typography variant="body2">Sin actas en esa reunión</Typography>
                  ) : (
                    group.docs.map((doc, index) => (
                      <Box key={`prev-${group.meetingId}-${index}`} display="flex" alignItems="center" justifyContent="space-between" p={1} border={1} borderColor="divider" mb={1}>
                        <Typography>{doc.fileName || `Acta previa ${index + 1}`}</Typography>
                        <Button size="small" variant="outlined" onClick={() => handleDownloadDocument(doc)}>
                          Descargar
                        </Button>
                      </Box>
                    ))
                  )}
                </Box>
              ))
            )}
          </Box>
          {actas.length === 0 ? (
            <Typography>No hay actas adjuntas</Typography>
          ) : (
            <Box>
              {actas.map((acta, index) => (
                <Box key={index} display="flex" alignItems="center" justifyContent="space-between" p={1} border={1} borderColor="divider" mb={1}>
                  <Typography>{acta.fileName || `Acta ${index + 1}`}</Typography>
                  <Button size="small" variant="outlined" onClick={() => handleDownloadDocument(acta)}>
                    Descargar
                  </Button>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenActaDialog(false)} sx={{ '&:hover': { color: 'error.main', backgroundColor: 'transparent' } }}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Rechazar */}
      <Dialog open={openRejectDialog} onClose={() => setOpenRejectDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Rechazar propuesta</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Propuesta #{selectedProposal?.id}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Motivo del rechazo"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRejectDialog(false)} disabled={actionLoading}>Cancelar</Button>
          <Button 
            onClick={handleReject} 
            color="error" 
            variant="contained"
            disabled={!rejectionReason.trim() || actionLoading}
          >
            Rechazar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Cerrar */}
      <Dialog open={openCloseDialog} onClose={() => setOpenCloseDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Cerrar propuesta #{selectedProposal?.id}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={closeForm.timeReduction}
                    onChange={(e) => setCloseForm({...closeForm, timeReduction: e.target.checked})}
                  />
                }
                label="Reducción tiempos ejecución"
              />
            </Grid>
            {closeForm.timeReduction && (
              <>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Horas por unidad"
                    value={closeForm.hoursPerUnit}
                    inputProps={{ inputMode: 'decimal', step: '0.01', min: 0 }}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (/^\d*(?:[.,]\d*)?$/.test(v)) {
                        setCloseForm({ ...closeForm, hoursPerUnit: v.replace(',', '.') });
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Unidades año"
                    value={closeForm.unitsPerYear}
                    inputProps={{ inputMode: 'numeric', step: '1', min: 0 }}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (/^\d*$/.test(v)) {
                        setCloseForm({ ...closeForm, unitsPerYear: v });
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Total Horas año"
                    value={closeForm.hoursPerUnit * closeForm.unitsPerYear}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
              </>
            )}
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={closeForm.costReduction}
                    onChange={(e) => setCloseForm({...closeForm, costReduction: e.target.checked})}
                  />
                }
                label="Reducción costes"
              />
            </Grid>
            {closeForm.costReduction && (
              <>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Unidad euros"
                    value={closeForm.eurosPerUnit}
                    inputProps={{ inputMode: 'decimal', step: '0.01', min: 0 }}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (/^\d*(?:[.,]\d*)?$/.test(v)) {
                        setCloseForm({ ...closeForm, eurosPerUnit: v.replace(',', '.') });
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Unidad año"
                    value={closeForm.unitsPerYearCost}
                    inputProps={{ inputMode: 'numeric', step: '1', min: 0 }}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (/^\d*$/.test(v)) {
                        setCloseForm({ ...closeForm, unitsPerYearCost: v });
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Total Euros año"
                    value={closeForm.eurosPerUnit * closeForm.unitsPerYearCost}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCloseDialog(false)} disabled={actionLoading}>Cancelar</Button>
          <Button 
            onClick={handleClose} 
            color="success" 
            variant="contained"
            disabled={actionLoading}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Documentos */}
      <Dialog open={openDocsDialog} onClose={() => setOpenDocsDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Documentos - Propuesta #{selectedProposal?.id}</DialogTitle>
        <DialogContent>
          {documents.length === 0 ? (
            <Typography>No hay documentos adjuntos</Typography>
          ) : (
            <Box>
              {documents.map((doc, index) => (
                <Box key={index} display="flex" alignItems="center" justifyContent="space-between" p={1} border={1} borderColor="divider" mb={1}>
                  <Typography>{doc.fileName || `Documento ${index + 1}`}</Typography>
                  <Button size="small" variant="outlined" onClick={() => handleDownloadDocument(doc)}>
                    Descargar
                  </Button>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDocsDialog(false)} sx={{ '&:hover': { color: 'error.main', backgroundColor: 'transparent' } }}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Backdrop de subida de actas */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 1, flexDirection: 'column' }}
        open={uploadingActa}
      >
        <CircularProgress color="inherit" thickness={5} />
        <Typography variant="body2" sx={{ mt: 2 }}>
          Subiendo acta{uploadTotal > 1 ? 's' : ''}… {uploadTotal > 0 ? `${uploadDone}/${uploadTotal}` : ''}
        </Typography>
      </Backdrop>

      {/* Backdrop de acciones (rechazar, cerrar, ver documentos) */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 2, flexDirection: 'column' }}
        open={actionLoading}
      >
        <CircularProgress color="inherit" thickness={5} />
        <Typography variant="body2" sx={{ mt: 2 }}>
          {actionMessage || 'Cargando...'}
        </Typography>
      </Backdrop>
    </Box>
  );
}


